import { useWatch } from 'react-hook-form';
import { z } from 'zod';

import { createLog, DayOfWeek, LogSeverity } from '@ekhodealer/ekho-common';
import { hoursSchema } from '@ekhodealer/ekho-common';
import {
	Box,
	Form,
	Group,
	Stack,
} from '@ekhodealer/ekho-common/rev';

import type { ServicesProvider, SupportHours } from '@ekhodealer/ekho-common';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { formatTimeString, parseTimeString } from '@common/lib/time';

import { useUpdateBusinessDetails } from '../../api/configuration/business-details/useUpdateBusinessDetails';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(servicesProvider: ServicesProvider | undefined | null): {
	supportHours: Record<
		string,
		{
			isOpen: boolean;
			openTime: { hour: number; minute: number };
			closeTime: { hour: number; minute: number };
		}
	>;
} {
	// Default placeholder times for closed days (not validated when isOpen is false)
	const defaultTime = { hour: 9, minute: 0 };
	const defaultCloseTime = { hour: 17, minute: 0 };

	const createSupportHourEntry = (
		dayOfWeek: string,
		schedule: Record<string, SupportHours | undefined>,
	) => {
		const daySchedule = schedule[dayOfWeek];
		if (!daySchedule) {
			// Return day object with isOpen: false and placeholder times
			// The schema validation only checks times when isOpen is true
			return {
				isOpen: false,
				openTime: defaultTime,
				closeTime: defaultCloseTime,
			};
		}
		const openTime = parseTimeString(daySchedule.open);
		const closeTime = parseTimeString(daySchedule.close);

		// If parsing failed, return closed day with placeholder times
		if (!openTime || !closeTime) {
			return {
				isOpen: false,
				openTime: defaultTime,
				closeTime: defaultCloseTime,
			};
		}

		return {
			isOpen: true,
			openTime,
			closeTime,
		};
	};

	// Access support from the provider (only exists on specific provider types like ServicesProviderSale, ServicesProviderDelivery, etc.)
	const support = (
		servicesProvider as ServicesProvider & {
			support?: { schedule?: Record<string, SupportHours> };
		}
	)?.support;
	const schedule = support?.schedule || {};
	const supportHours = {
		[DayOfWeek.MONDAY]: createSupportHourEntry(DayOfWeek.MONDAY, schedule),
		[DayOfWeek.TUESDAY]: createSupportHourEntry(DayOfWeek.TUESDAY, schedule),
		[DayOfWeek.WEDNESDAY]: createSupportHourEntry(DayOfWeek.WEDNESDAY, schedule),
		[DayOfWeek.THURSDAY]: createSupportHourEntry(DayOfWeek.THURSDAY, schedule),
		[DayOfWeek.FRIDAY]: createSupportHourEntry(DayOfWeek.FRIDAY, schedule),
		[DayOfWeek.SATURDAY]: createSupportHourEntry(DayOfWeek.SATURDAY, schedule),
		[DayOfWeek.SUNDAY]: createSupportHourEntry(DayOfWeek.SUNDAY, schedule),
	};
	return {
		supportHours,
	};
}

/**
 * Helper function to get Partial<ServicesProvider> from form data
 */
function getServicesProviderFromFormData(
	data: {
		supportHours: Record<
			string,
			{
				isOpen: boolean;
				openTime: { hour: number; minute: number };
				closeTime: { hour: number; minute: number };
			} | null
		>;
	},
	currentServicesProvider: ServicesProvider,
): Partial<ServicesProvider> {
	const supportHours = data.supportHours;

	const createSupportScheduleEntry = (dayOfWeek: DayOfWeek) => {
		const daySchedule = supportHours[dayOfWeek];

		if (!daySchedule?.isOpen || !daySchedule.openTime || !daySchedule.closeTime) {
			return null;
		}

		return {
			day: dayOfWeek,
			open: formatTimeString(daySchedule.openTime.hour, daySchedule.openTime.minute),
			close: formatTimeString(daySchedule.closeTime.hour, daySchedule.closeTime.minute),
		};
	};

	// Access support from the provider (only exists on specific provider types like ServicesProviderSale, ServicesProviderDelivery, etc.)
	const currentSupport = (
		currentServicesProvider as ServicesProvider & {
			support?: { email?: string; phone?: string; ekhoPhone?: string };
		}
	)?.support;

	const scheduleEntries = [
		[DayOfWeek.MONDAY, createSupportScheduleEntry(DayOfWeek.MONDAY)],
		[DayOfWeek.TUESDAY, createSupportScheduleEntry(DayOfWeek.TUESDAY)],
		[DayOfWeek.WEDNESDAY, createSupportScheduleEntry(DayOfWeek.WEDNESDAY)],
		[DayOfWeek.THURSDAY, createSupportScheduleEntry(DayOfWeek.THURSDAY)],
		[DayOfWeek.FRIDAY, createSupportScheduleEntry(DayOfWeek.FRIDAY)],
		[DayOfWeek.SATURDAY, createSupportScheduleEntry(DayOfWeek.SATURDAY)],
		[DayOfWeek.SUNDAY, createSupportScheduleEntry(DayOfWeek.SUNDAY)],
	];

	// Build schedule object - include null values for closed days so backend can delete them
	const schedule: Record<string, SupportHours | null> = Object.fromEntries(scheduleEntries);

	const result: Partial<ServicesProvider> = {
		support: {
			email: currentSupport?.email || '',
			phone: currentSupport?.phone || '',
			ekhoPhone: currentSupport?.ekhoPhone || '',
			schedule,
		},
	} as Partial<ServicesProvider>;

	return result;
}

const businessHoursSchema = z.object({
	supportHours: hoursSchema,
});

type BusinessHoursFormData = z.infer<typeof businessHoursSchema>;

export function BusinessHoursForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const updateBusinessDetailsMutation = useUpdateBusinessDetails();

	// Handle form submit
	const handleSubmit = async (data: BusinessHoursFormData): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		try {
			const updatedServicesProvider = getServicesProviderFromFormData(data, servicesProvider);
			await updateBusinessDetailsMutation.mutateAsync({
				servicesProvider: updatedServicesProvider,
			});
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update business hours: ${error instanceof Error ? error.message : String(error)}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='business-hours'
				schema={businessHoursSchema}
				defaultValues={getDefaultValues(servicesProvider)}
				onSubmit={handleSubmit}
				title='Weekly schedule'
				titleLevel='subgroup'
				titleWeight='semibold'
				showSaveAndClear>
				<BusinessHoursFormFields />
			</Form>
		</Box>
	);
}

/**
 * Component that renders the form fields for each day of the week
 */
function BusinessHoursFormFields(): React.ReactNode {
	const supportHours = useWatch({ name: 'supportHours' });

	const days = [
		{ key: DayOfWeek.MONDAY, label: 'Monday' },
		{ key: DayOfWeek.TUESDAY, label: 'Tuesday' },
		{ key: DayOfWeek.WEDNESDAY, label: 'Wednesday' },
		{ key: DayOfWeek.THURSDAY, label: 'Thursday' },
		{ key: DayOfWeek.FRIDAY, label: 'Friday' },
		{ key: DayOfWeek.SATURDAY, label: 'Saturday' },
		{ key: DayOfWeek.SUNDAY, label: 'Sunday' },
	];

	return (
		<Stack itemsSpacing='16'>
			{days.map((day) => {
				const dayData = supportHours?.[day.key];
				const isOpen = dayData?.isOpen ?? false;

				return (
					<Group key={day.key} itemsSpacing='16' itemsAlignX='start' itemsAlignY='start'>
						<Form.Field.Switch
							schemaField={`supportHours.${day.key}.isOpen`}
							labelText={day.label}
							orientation='horizontal'
						/>
						{isOpen && (
							<Group itemsAlignX='start' itemsSpacing='24'>
								<Group.Item>
									<Form.Field.Time
										schemaField={`supportHours.${day.key}.openTime`}
										labelText='Open'
										required
									/>
								</Group.Item>
								<Group.Item>
									<Form.Field.Time
										schemaField={`supportHours.${day.key}.closeTime`}
										labelText='Close'
										required
									/>
								</Group.Item>
							</Group>
						)}
					</Group>
				);
			})}
		</Stack>
	);
}

export default BusinessHoursForm;
